import React from 'react';

interface LandingEditorialProps {
  onCreateCampaign: () => void;
  onSeeHowItWorks?: () => void;
}

// SVG Narrative Illustration — a campaign moment
const NarrativeIllustration = () => (
  <svg
    viewBox="0 0 600 400"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      width: '100%',
      height: '100%',
      display: 'block',
    }}
  >
    {/* Background gradient */}
    <defs>
      <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: '#1a1f2e', stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: '#0f1419', stopOpacity: 1 }} />
      </linearGradient>
      <linearGradient id="lightGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: '#f4d03f', stopOpacity: 0.2 }} />
        <stop offset="100%" style={{ stopColor: '#d4534f', stopOpacity: 0.15 }} />
      </linearGradient>
    </defs>

    <rect width="600" height="400" fill="url(#bgGradient)" />

    {/* Ambient light glow */}
    <circle cx="300" cy="150" r="180" fill="url(#lightGradient)" opacity="0.4" />

    {/* Archway stone structure */}
    <path
      d="M 100 250 L 150 100 L 450 100 L 500 250"
      stroke="#4a5568"
      strokeWidth="8"
      fill="none"
      opacity="0.7"
    />
    <line x1="100" y1="250" x2="500" y2="250" stroke="#3d4557" strokeWidth="4" />

    {/* Left pillar detail */}
    <rect x="85" y="250" width="30" height="130" fill="#0f1419" stroke="#4a5568" strokeWidth="2" />

    {/* Right pillar detail */}
    <rect x="485" y="250" width="30" height="130" fill="#0f1419" stroke="#4a5568" strokeWidth="2" />

    {/* Central altar / pedestal */}
    <ellipse cx="300" cy="280" rx="50" ry="20" fill="#1a1f2e" stroke="#4a5568" strokeWidth="2" />
    <rect x="280" y="280" width="40" height="60" fill="#0f1419" stroke="#4a5568" strokeWidth="2" />

    {/* Two ceremonial chalices */}
    <g id="chalice-left">
      <ellipse cx="240" cy="270" rx="15" ry="8" fill="none" stroke="#f4d03f" strokeWidth="2" />
      <path
        d="M 235 270 L 230 300 Q 230 310 240 310 Q 250 310 250 300 L 245 270"
        fill="none"
        stroke="#f4d03f"
        strokeWidth="2"
      />
      <circle cx="240" cy="300" r="8" fill="#f4d03f" opacity="0.5" />
    </g>

    <g id="chalice-right">
      <ellipse cx="360" cy="270" rx="15" ry="8" fill="none" stroke="#f4d03f" strokeWidth="2" />
      <path
        d="M 355 270 L 350 300 Q 350 310 360 310 Q 370 310 370 300 L 365 270"
        fill="none"
        stroke="#f4d03f"
        strokeWidth="2"
      />
      <circle cx="360" cy="300" r="8" fill="#f4d03f" opacity="0.5" />
    </g>

    {/* Main character silhouette — observing */}
    <g id="character-main">
      {/* Head */}
      <circle cx="200" cy="180" r="16" fill="#fef8f0" opacity="0.8" />
      {/* Body */}
      <rect x="195" y="200" width="10" height="40" fill="#c4a77d" opacity="0.7" />
      {/* Left arm */}
      <line x1="195" y1="210" x2="170" y2="220" stroke="#c4a77d" strokeWidth="4" opacity="0.7" />
      {/* Right arm extended */}
      <line x1="205" y1="210" x2="240" y2="200" stroke="#c4a77d" strokeWidth="4" opacity="0.7" />
      {/* Legs */}
      <line x1="195" y1="240" x2="185" y2="280" stroke="#7a6b5d" strokeWidth="3" opacity="0.7" />
      <line x1="205" y1="240" x2="215" y2="280" stroke="#7a6b5d" strokeWidth="3" opacity="0.7" />
    </g>

    {/* Shadowy figure in background — NPC/Witness */}
    <g id="character-npc" opacity="0.5">
      <circle cx="420" cy="160" r="12" fill="#d4cfc4" />
      <rect x="416" y="176" width="8" height="30" fill="#7a6b5d" />
      <line x1="416" y1="185" x2="400" y2="195" stroke="#7a6b5d" strokeWidth="3" />
      <line x1="424" y1="185" x2="440" y2="195" stroke="#7a6b5d" strokeWidth="3" />
    </g>

    {/* Floating rune / magical symbol */}
    <g id="rune" opacity="0.6">
      <circle cx="450" cy="140" r="20" fill="none" stroke="#f4d03f" strokeWidth="1.5" />
      <path
        d="M 450 125 L 450 155 M 435 140 L 465 140"
        stroke="#f4d03f"
        strokeWidth="1"
      />
      <circle cx="450" cy="140" r="3" fill="#f4d03f" />
    </g>

    {/* Decorative corner markers */}
    <line x1="110" y1="110" x2="130" y2="110" stroke="#f4d03f" strokeWidth="1.5" opacity="0.7" />
    <line x1="110" y1="110" x2="110" y2="130" stroke="#f4d03f" strokeWidth="1.5" opacity="0.7" />

    <line x1="490" y1="110" x2="470" y2="110" stroke="#f4d03f" strokeWidth="1.5" opacity="0.7" />
    <line x1="490" y1="110" x2="490" y2="130" stroke="#f4d03f" strokeWidth="1.5" opacity="0.7" />

    {/* Bottom ornamental line */}
    <line x1="100" y1="360" x2="500" y2="360" stroke="#4a5568" strokeWidth="1.5" opacity="0.6" />
  </svg>
);

export function LandingEditorial({
  onCreateCampaign,
  onSeeHowItWorks,
}: LandingEditorialProps) {
  return (
    <div className="landing-editorial-root">
      {/* Hero Section */}
      <div className="landing-hero-editorial">
        {/* Left Column — Copy */}
        <div className="landing-hero-editorial-left">
          <div>
            <div className="hero-tagline">A New Kind of Learning</div>
            <h1>
              Experience{' '}
              <span className="gradient-word">what you learn</span>
            </h1>
          </div>

          <p className="subtitle">
            Some ideas stay abstract until you have something to live through.
            Enter a story, make choices, get things wrong, and let the concept
            become part of the experience.
          </p>

          {/* CTA Buttons */}
          <div className="cta-group">
            <button className="cta-primary" onClick={onCreateCampaign}>
              Start a Quest
            </button>
            {onSeeHowItWorks && (
              <button className="cta-secondary" onClick={onSeeHowItWorks}>
                See how it works
              </button>
            )}
          </div>

          {/* Editorial tags */}
          <div className="hero-tags">
            <span className="editorial-tag">Learning</span>
            <span className="editorial-tag">Narrative</span>
            <span className="editorial-tag">AI</span>
          </div>
        </div>

        {/* Right Column — Illustration */}
        <div className="landing-hero-editorial-right">
          <div className="narrative-frame">
            <NarrativeIllustration />
          </div>

          {/* Caption below illustration */}
          <div className="illustration-caption">
            <p className="page-label">
              CHAPTER I — THE BEGINNING
            </p>
            <p className="illustration-subtitle">
              Every campaign is a story you live, not just read.
            </p>
          </div>
        </div>
      </div>

      {/* Secondary Section — Features */}
      <div className="features-section">
        <div className="divider-editorial" />

        <div className="how-it-works-section">
          <h2>How It Works</h2>

          <div className="features-editorial">
            <div className="feature-item">
              <div className="feature-item-icon">🎭</div>
              <div className="feature-item-content">
                <h4>Multiple Narratives</h4>
                <p>
                  From D&D fantasy to science fiction, investigation to horror—each
                  genre is a different lens for learning.
                </p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-item-icon">🤖</div>
              <div className="feature-item-content">
                <h4>AI Game Master</h4>
                <p>
                  An intelligent narrator responds to your choices, adapts to your
                  decisions, and keeps the story coherent and engaging.
                </p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-item-icon">📖</div>
              <div className="feature-item-content">
                <h4>Living Memory</h4>
                <p>
                  Your campaigns become part of your knowledge. What you experience
                  stays with you far longer than what you read.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="footer-cta">
        <div className="divider-editorial" />
        <h3>Ready to Begin?</h3>
        <p className="footer-subtitle">
          You can create up to 3 campaigns. Each one is a unique adventure.
        </p>
        <button className="cta-primary footer-button" onClick={onCreateCampaign}>
          Start Your First Quest
        </button>

        <p className="footer-branding">
          <span className="page-label">LEARN BY CAMPAIGN</span> — Premium narrative learning
        </p>
      </div>
    </div>
  );
}
